import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';

import { AuthProvider } from '@/contexts/AuthContext';
import { TripPreferencesProvider } from '@/contexts/TripPreferencesContext';

import { SiteNav } from '@/components/ui/site-nav';
import { logPerformanceMetrics } from '@/lib/performance';
// import Header from '../components/Header';
import { Footer } from '@/components/Footer';

import { ThemeProvider } from '@/components/theme-provider';
// import { RouteTransition } from '@/components/transitions';
import { NotFound } from '@/components/NotFound';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
    },
  },
});

function RootComponent() {
  const { location } = useRouterState();
  // Hide footer on trip view pages (story, itinerary, traditional, map) — these are immersive full-screen layouts
  const showFooter = !/\/trip\/[^/]+\/(story|itinerary|traditional|map)/.test(location.pathname);

  // Initialize performance monitoring in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      logPerformanceMetrics();
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TripPreferencesProvider>
          <ThemeProvider defaultTheme="light" storageKey="wundra-theme">
            <div className="flex flex-col min-h-screen bg-background text-foreground">
              {/* Navigation */}
              <div className="fixed top-0 left-0 right-0 z-50">
                <SiteNav />
              </div>

              {/* Main Content */}
              <main className="flex-1 pt-16">
                {/* Removed RouteTransition to improve performance */}
                <div>
                  <Outlet />
                </div>
              </main>

              {/* Footer — hidden on immersive trip view pages */}
              {showFooter && <Footer />}

              {/* Development Tools */}

              {process.env.NODE_ENV === 'development' && (
                <>
                  <TanStackRouterDevtools position="bottom-left" />
                  {/* <ReactQueryDevtools
                    initialIsOpen={false}
                    position="bottom-right"
                  /> */}
                </>
              )}

              {/* Toast Notifications */}
              <Toaster
                position="top-right"
                richColors
                closeButton
                duration={4000}
                className="z-[9999]"
              />
            </div>
          </ThemeProvider>
        </TripPreferencesProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export const Route = createRootRoute({
  notFoundComponent: NotFound,
  component: RootComponent,
});
